import { FlaggableIngredient } from 'common/Constants';
import { isMealPlanDessert } from 'common/MealPlanUtils';
import { isNil } from 'lodash';
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  DishTag,
  FoodItem,
  MealPlanFoodItemUpsellFragment,
  MealPlanFoodType,
  UpsellTier,
} from 'src/gqlReactTypings.generated.d';
import { CardType, ServingChip } from 'src/pages/consumer/meal-plans/components/MealPlanDishCard';
import { ExpandableTextSimple } from 'src/shared/design-system/ExpandableText';
import { DIETARY_TAG_DISPLAY_ORDER, dietaryTagToFoodItemFilter } from 'src/shared/utils/diet-utils';
import { DisplayedFoodItemTags } from 'src/shared/utils/IconUtils';
import { DietaryTags as DietaryTagsEnum } from 'web-common/src/shared/Enums';
import { FoodName } from '../stylesV2';
import { AllergyWarning } from './AllergyWarning';
import { DetailsTabs } from './DetailsTabs';
import { ModalV2FoodTags } from './ModalV2FoodTags';

interface IModalV2InfoContentProps {
  foodItem: Pick<FoodItem, 'description' | 'ingredients' | 'name' | 'id'> &
    DisplayedFoodItemTags & {
      ingredientsFlagged?: FlaggableIngredient[];
      customIngredientsFlagged?: string[] | null;
      isSeedOilFree?: boolean | null;
      tags?: DishTag[] | null;
      mealPlanFoodType?: MealPlanFoodType | null;
    };
  cuisines?: string;
  averageRating?: number;
  ratingCount?: number;
  shefMenuUrl?: string;
  publicName?: string;
  upsell?: MealPlanFoodItemUpsellFragment | null;
  cardType?: CardType | null;
}

enum DishDetailsTab {
  DESCRIPTION = 'Description',
  MAIN_INGREDIENTS = 'Main ingredients',
}

export const ModalV2InfoContent: React.FC<React.PropsWithChildren<IModalV2InfoContentProps>> = ({
  foodItem,
  cuisines,
  averageRating,
  ratingCount,
  shefMenuUrl,
  publicName,
  upsell,
  children,
}) => {
  const ORGANIC_UPSELL_TIERS = new Set([
    UpsellTier.OrganicVegetarian,
    UpsellTier.FullOrganic,
    UpsellTier.OrganicProtein,
  ]);
  const hasOrganicUpsell = upsell?.tier && ORGANIC_UPSELL_TIERS.has(upsell.tier);

  const dietaryTags = useMemo(
    () =>
      DIETARY_TAG_DISPLAY_ORDER.filter(
        (dietaryTag) =>
          dietaryTagToFoodItemFilter[dietaryTag](foodItem) &&
          !(hasOrganicUpsell && dietaryTag === DietaryTagsEnum.ORGANIC)
      ),
    [foodItem, hasOrganicUpsell]
  );
  const detailsTabs = {
    [DishDetailsTab.DESCRIPTION]: <ExpandableTextSimple>{foodItem.description ?? ''}</ExpandableTextSimple>,
    [DishDetailsTab.MAIN_INGREDIENTS]: (
      <>
        <span>{foodItem.ingredients}</span>
        <AllergyWarning />
      </>
    ),
  };

  return (
    <>
      <FoodName.Container>
        {cuisines && <FoodName.Cuisines>{cuisines}</FoodName.Cuisines>}
        <FoodName.Name>{foodItem.name}</FoodName.Name>
        <ModalV2FoodTags
          upsell={upsell}
          dietaryTags={dietaryTags}
          averageRating={averageRating}
          ratingCount={ratingCount}
          foodItemId={foodItem.id}
          ingredientsFlagged={foodItem.ingredientsFlagged}
          customIngredientsFlagged={foodItem.customIngredientsFlagged ?? []}
          isSeedOilFree={foodItem.isSeedOilFree}
          tags={foodItem.tags}
        />
        {!isNil(shefMenuUrl) && !isNil(publicName) && (
          <FoodName.ByShef>
            by <Link to={shefMenuUrl}>{publicName}</Link>
          </FoodName.ByShef>
        )}
      </FoodName.Container>
      {children}
      <DetailsTabs tabs={detailsTabs} className='my-6' />
      {isMealPlanDessert(foodItem.mealPlanFoodType) && (
        <div className='flex items-center justify-between border-t border-solid border-neutral-100 py-4'>
          <p className='m-0 text-h6-bold text-eggplant'>Servings</p>
          <ServingChip numServings={1} mealPlanFoodType={foodItem.mealPlanFoodType} />
        </div>
      )}
    </>
  );
};
